// First-party attribution capture for the app (dashboard / signup) funnel.
//
// The published merchant pages run assets/analytics/beacon.js, which persists
// `pj_attr` and exposes window.pjAttribution(). The app SPA is a different
// origin and does NOT run that beacon, so we capture our own touch here: on
// first landing we read utm_*/gclid/fbclid + referrer, mint a durable
// visitorId, and persist first-touch + last-touch under `pj_app_attr`.
//
// getStoredAttribution() is then merged into the subscription checkout metadata
// (stripe.service.getCheckoutUrl) so a new subscription can be traced back to
// where the signup came from.

const ATTR_KEY = 'pj_app_attr'
const VID_KEY = 'pj_app_vid'

export interface AttributionTouch {
  source: string
  medium: string
  campaign: string
  content: string
  term: string
  gclid: string
  fbclid: string
  referrer: string
  landingPath: string
  ts: string
}

export interface Attribution {
  visitorId: string
  firstTouch: AttributionTouch | null
  lastTouch: AttributionTouch | null
}

function hostOf(url: string): string {
  try { return new URL(url).host } catch { return '' }
}

function isInternalReferrer(ref: string): boolean {
  if (!ref) return true
  try { return new URL(ref).host === location.host } catch { return false }
}

function mintVisitorId(): string {
  let vid = ''
  try { vid = localStorage.getItem(VID_KEY) || '' } catch { /* ignore */ }
  if (!vid) {
    vid = (window.crypto && 'randomUUID' in window.crypto)
      ? window.crypto.randomUUID()
      : `v-${Date.now()}-${Math.random().toString(36).slice(2)}`
    try { localStorage.setItem(VID_KEY, vid) } catch { /* ignore */ }
  }
  return vid
}

// A touch only exists when something actually points here: a UTM tag, a paid
// click id, or an external referrer. Internal navigation returns null so we
// never overwrite a real source with a same-site pageview.
function buildTouch(): AttributionTouch | null {
  const qp = new URLSearchParams(location.search)
  const p = (k: string) => qp.get(k) || ''
  const src = p('utm_source')
  const gclid = p('gclid')
  const fbclid = p('fbclid')
  const ref = document.referrer || ''
  if (!src && !gclid && !fbclid && isInternalReferrer(ref)) return null
  return {
    source: src || (gclid ? 'google' : (fbclid ? 'facebook' : '')) || hostOf(ref),
    medium: p('utm_medium') || (gclid ? 'cpc' : (fbclid ? 'paid_social' : (ref ? 'referral' : ''))),
    campaign: p('utm_campaign'),
    content: p('utm_content'),
    term: p('utm_term'),
    gclid,
    fbclid,
    referrer: ref,
    landingPath: location.pathname,
    ts: new Date().toISOString(),
  }
}

/**
 * Capture attribution on app load. Preserves the original first-touch; refreshes
 * last-touch on every visit that carries a real source. Prefers the merchant
 * beacon's attribution when the page happens to expose it. Safe to call on
 * every boot.
 */
export function captureAttributionOnLoad(): void {
  try {
    // If the page carries the merchant beacon, mirror its object verbatim so
    // both funnels share ONE visitorId + touch.
    const w = window as unknown as { pjAttribution?: () => unknown }
    if (typeof w.pjAttribution === 'function') {
      const a = w.pjAttribution()
      if (a && typeof a === 'object') {
        localStorage.setItem(ATTR_KEY, JSON.stringify(a))
        return
      }
    }
  } catch { /* ignore */ }

  try {
    let attr: Attribution
    try {
      attr = JSON.parse(localStorage.getItem(ATTR_KEY) || 'null') as Attribution
    } catch { attr = null as unknown as Attribution }
    if (!attr || typeof attr !== 'object') {
      attr = { visitorId: '', firstTouch: null, lastTouch: null }
    }
    attr.visitorId = mintVisitorId()
    const touch = buildTouch()
    if (touch) {
      if (!attr.firstTouch) attr.firstTouch = touch
      attr.lastTouch = touch
    }
    localStorage.setItem(ATTR_KEY, JSON.stringify(attr))
  } catch { /* ignore — attribution is best-effort */ }
}

/**
 * Read the stored attribution object (or null). Merged into checkout metadata.
 */
export function getStoredAttribution(): Attribution | null {
  try {
    const raw = localStorage.getItem(ATTR_KEY)
    if (!raw) return null
    const attr = JSON.parse(raw)
    return (attr && typeof attr === 'object' && !Array.isArray(attr)) ? attr : null
  } catch {
    return null
  }
}
